import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import { InitiativeTabsSection } from '../SharedInitiativeTabs';
import './growthForm.scss';

const emptyMetric = (unit = 'MT', unit2 = '%') => ({
  value: '',
  unit,
  unit2,
});

const buildInitialFilters = (data = {}, mode = 'edit') => {
  const now = new Date();
  const createdOn =
    data.created_on ||
    now.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return {
    kpi: data.project_objective || data.kpi || '',
    project_id: data.project_id || '',
    project_description: data.project_description || '',
    account_owner: data.account_owner || '',
    account_owner_title: data.account_owner_title || '',
    report_to: data.report_to || '',
    report_to_title: data.report_to_title || '',
    created_on: createdOn,
    net_improvement: data.net_improvement || emptyMetric('%', 'Unit'),
    expected_savings: data.expected_savings || emptyMetric('Rs', 'MT'),
    previous_actual: data.previous_actual || emptyMetric('MT', '%'),
    current_plan: data.current_plan || emptyMetric('MT', '%'),
    current_target: data.current_target || emptyMetric('MT', '%'),
    enabling_one_description:
      data.enabling_one_description ||
      (mode === 'edit' ? 'Support focus-market activation and coverage improvement.' : ''),
    enabling_two_description:
      data.enabling_two_description ||
      (mode === 'edit' ? 'Track weekly progress and escalate delayed actions.' : ''),
  };
};

const NumberWithUnit = ({ label, value, unit, unit2, onValueChange, onUnitChange, onUnit2Change, disabled }) => (
  <Box className="number-unit">
    <TextField
      label={label}
      size="small"
      fullWidth
      type="number"
      value={value === undefined || value === null ? '' : value}
      onChange={(e) => onValueChange(e.target.value)}
      disabled={disabled}
    />
    <TextField
      className="unit-field"
      label="Unit"
      size="small"
      value={unit || ''}
      onChange={(e) => onUnitChange(e.target.value)}
      disabled={disabled}
    />
    <TextField
      className="unit-field"
      label="Unit"
      size="small"
      value={unit2 || ''}
      onChange={(e) => onUnit2Change(e.target.value)}
      disabled={disabled}
    />
  </Box>
);

const GrowthForm = ({
  mode = 'edit',
  initialData = {},
  onBack,
  onSave,
}) => {
  const seed = useMemo(() => buildInitialFilters(initialData, mode), [initialData, mode]);
  const [filters, setFilters] = useState(seed);
  const [expanded, setExpanded] = useState(true);
  const [locked, setLocked] = useState(Boolean(initialData.is_locked));
  const [cancelOpen, setCancelOpen] = useState(false);

  useEffect(() => {
    setFilters(buildInitialFilters(initialData, mode));
    setLocked(Boolean(initialData.is_locked));
  }, [initialData, mode]);

  const updateField = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updateMetric = (key, patch) => {
    setFilters((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...patch },
    }));
  };

  const handleSaveDraft = () => {
    onSave({
      ...initialData,
      ...filters,
      project_objective: filters.kpi,
      is_locked: locked,
    });
  };

  const handleRevert = () => {
    setFilters(buildInitialFilters(initialData, mode));
    setCancelOpen(false);
  };

  return (
    <div className="growth-form-page">
      <Box className="back-row">
        <Button variant="outlined" size="small" className="back-btn" onClick={onBack}>
          Back
        </Button>
      </Box>

      <Box display="flex" alignItems="center" mb={1}>
        <Typography className="form-title">
          {filters.project_id || 'New'} - Growth Initiatives Form
        </Typography>
        <Tooltip title={expanded ? 'Collapse Form' : 'Expand Form'}>
          <IconButton size="small" onClick={() => setExpanded((v) => !v)} sx={{ ml: 1, color: '#707172' }}>
            {expanded ? (
              <ExpandLessIcon
                sx={{
                  backgroundColor: '#707172',
                  color: '#fff',
                  borderRadius: '50%',
                  fontSize: 20,
                  p: '2px',
                }}
              />
            ) : (
              <ExpandCircleDownIcon />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={expanded}>
        <Box className="form-card">
          <Box className="form-grid">
            <Box className="form-col">
              <Box className="two-col">
                <Box display="flex" flexDirection="column" gap={1.75}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Project Objectives"
                    value={filters.kpi}
                    onChange={(e) => updateField('kpi', e.target.value)}
                    disabled={locked}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Report to"
                    value={filters.report_to}
                    onChange={(e) => updateField('report_to', e.target.value)}
                    disabled={locked}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Account owner"
                    value={filters.account_owner}
                    onChange={(e) => updateField('account_owner', e.target.value)}
                    disabled={locked}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Project Description"
                    value={filters.project_description}
                    onChange={(e) => updateField('project_description', e.target.value)}
                    disabled={locked}
                  />
                  <NumberWithUnit
                    label="Net Improvement"
                    value={filters.net_improvement?.value}
                    unit={filters.net_improvement?.unit}
                    unit2={filters.net_improvement?.unit2}
                    disabled={locked}
                    onValueChange={(val) => updateMetric('net_improvement', { value: val })}
                    onUnitChange={(unit) => updateMetric('net_improvement', { unit })}
                    onUnit2Change={(unit2) => updateMetric('net_improvement', { unit2 })}
                  />
                  <NumberWithUnit
                    label="Expected Savings"
                    value={filters.expected_savings?.value}
                    unit={filters.expected_savings?.unit}
                    unit2={filters.expected_savings?.unit2}
                    disabled={locked}
                    onValueChange={(val) => updateMetric('expected_savings', { value: val })}
                    onUnitChange={(unit) => updateMetric('expected_savings', { unit })}
                    onUnit2Change={(unit2) => updateMetric('expected_savings', { unit2 })}
                  />
                </Box>

                <Box display="flex" flexDirection="column" gap={1.75}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Project ID"
                    value={filters.project_id}
                    disabled
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Title"
                    value={filters.account_owner_title}
                    onChange={(e) => updateField('account_owner_title', e.target.value)}
                    disabled={locked}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Title"
                    value={filters.report_to_title}
                    onChange={(e) => updateField('report_to_title', e.target.value)}
                    disabled={locked}
                  />
                  <NumberWithUnit
                    label="FY26 Actual"
                    value={filters.previous_actual?.value}
                    unit={filters.previous_actual?.unit}
                    unit2={filters.previous_actual?.unit2}
                    disabled={locked}
                    onValueChange={(val) => updateMetric('previous_actual', { value: val })}
                    onUnitChange={(unit) => updateMetric('previous_actual', { unit })}
                    onUnit2Change={(unit2) => updateMetric('previous_actual', { unit2 })}
                  />
                  <NumberWithUnit
                    label="FY27 Plan"
                    value={filters.current_plan?.value}
                    unit={filters.current_plan?.unit}
                    unit2={filters.current_plan?.unit2}
                    disabled={locked}
                    onValueChange={(val) => updateMetric('current_plan', { value: val })}
                    onUnitChange={(unit) => updateMetric('current_plan', { unit })}
                    onUnit2Change={(unit2) => updateMetric('current_plan', { unit2 })}
                  />
                  <NumberWithUnit
                    label="FY27 Target"
                    value={filters.current_target?.value}
                    unit={filters.current_target?.unit}
                    unit2={filters.current_target?.unit2}
                    disabled={locked}
                    onValueChange={(val) => updateMetric('current_target', { value: val })}
                    onUnitChange={(unit) => updateMetric('current_target', { unit })}
                    onUnit2Change={(unit2) => updateMetric('current_target', { unit2 })}
                  />
                </Box>
              </Box>
            </Box>

            <Box className="form-col">
              <Box maxWidth={280}>
                <TextField
                  size="small"
                  fullWidth
                  label="Created On"
                  value={filters.created_on}
                  disabled
                />
              </Box>
              <Box className="enabling-row">
                <Box>
                  <div className="enabling-label">Enabling Initiative 1</div>
                  <TextField
                    label="Initiative Description"
                    fullWidth
                    multiline
                    rows={4}
                    value={filters.enabling_one_description}
                    onChange={(e) => updateField('enabling_one_description', e.target.value)}
                    disabled={locked}
                  />
                </Box>
                <Box>
                  <div className="enabling-label">Enabling Initiative 2</div>
                  <TextField
                    label="Initiative Description"
                    fullWidth
                    multiline
                    rows={4}
                    value={filters.enabling_two_description}
                    onChange={(e) => updateField('enabling_two_description', e.target.value)}
                    disabled={locked}
                  />
                </Box>
              </Box>

              <Box className="actions-row">
                <Button
                  className="btn-lock"
                  variant="outlined"
                  color={locked ? 'error' : 'primary'}
                  onClick={() => setLocked((v) => !v)}
                  disabled={mode === 'create'}
                  startIcon={locked ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                >
                  {locked ? 'Unlock' : 'Lock'}
                </Button>
                <Button
                  className="btn-save"
                  variant="contained"
                  onClick={handleSaveDraft}
                  disabled={locked}
                >
                  Save Draft
                </Button>
                <Button
                  className="btn-cancel"
                  variant="contained"
                  color="error"
                  onClick={() => setCancelOpen(true)}
                  disabled={locked || mode === 'create'}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Collapse>

      <InitiativeTabsSection />

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)}>
        <DialogTitle>Confirm Cancel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revert changes? All unsaved edits will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} color="primary">
            No
          </Button>
          <Button onClick={handleRevert} color="error" variant="contained">
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GrowthForm;
